// src/components/Auth/AuthPage.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher.tsx';
import {
  auth,
  signInAnonymously,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from '../../config/firebase.ts';
import { useNavigate, useLocation } from 'react-router-dom';
import './AuthPage.css';

// Mode d'authentification
type AuthMode = 'signin' | 'signup' | 'reset';

// Le composant accepte maintenant "onLoginSuccess" en tant que prop
const AuthPage = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Si l'utilisateur est déjà connecté, le rediriger immédiatement
    if (auth.currentUser) {
      const pendingCode = localStorage.getItem('pendingLobbyCode');
      const redirectPath = localStorage.getItem('postLoginRedirect');
      
      if (pendingCode) {
        navigate(`/lobby/${pendingCode}`);
        localStorage.removeItem('pendingLobbyCode');
        return;
      }
      if (redirectPath) {
        navigate(redirectPath);
        localStorage.removeItem('postLoginRedirect');
        return;
      }
      // Si pas de redirection en attente, rediriger vers l'accueil
      navigate('/');
      return;
    }

    // Enregistrer une redirection post-login si l'utilisateur visite une page protégée
    try {
      const path = location?.pathname || '';
      const wantsSmartClash = path.startsWith('/smartclash');
      const wantsDaily = path.startsWith('/daily');
      const wantsSyncDuel = path.startsWith('/syncduel/');
      const wantsLobby = path.startsWith('/lobby/');
      if (!auth.currentUser && (wantsSmartClash || wantsDaily || wantsSyncDuel || wantsLobby)) {
        localStorage.setItem('postLoginRedirect', path + (location.search || ''));
      }
    } catch {}
  }, [auth.currentUser, navigate, location]);

  const handleAnonymousLogin = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setError(t('errorEnterPseudo'));
      return;
    }
    try {
      const userCredential = await signInAnonymously(auth);
      const user = userCredential.user;
      await updateProfile(user, { displayName: username });
      await user.reload();
      onLoginSuccess(auth.currentUser);

      // Redirection immédiate si pendingLobbyCode ou postLoginRedirect existe
      const pendingCode = localStorage.getItem('pendingLobbyCode');
      const redirectPath = localStorage.getItem('postLoginRedirect');
      if (pendingCode) {
        navigate(`/lobby/${pendingCode}`);
        localStorage.removeItem('pendingLobbyCode');
      } else if (redirectPath) {
        navigate(redirectPath);
        localStorage.removeItem('postLoginRedirect');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(t('errorMessageDefault'));
      console.error(err);
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      onLoginSuccess(result.user);
      
      // Gérer la redirection post-login
      const pendingCode = localStorage.getItem('pendingLobbyCode');
      const redirectPath = localStorage.getItem('postLoginRedirect');
      if (pendingCode) {
        navigate(`/lobby/${pendingCode}`);
        localStorage.removeItem('pendingLobbyCode');
      } else if (redirectPath) {
        navigate(redirectPath);
        localStorage.removeItem('postLoginRedirect');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(t('errorMessageDefault'));
      console.error(err);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      onLoginSuccess(userCredential.user);
      
      // Gérer la redirection post-login
      const pendingCode = localStorage.getItem('pendingLobbyCode');
      const redirectPath = localStorage.getItem('postLoginRedirect');
      if (pendingCode) {
        navigate(`/lobby/${pendingCode}`);
        localStorage.removeItem('pendingLobbyCode');
      } else if (redirectPath) {
        navigate(redirectPath);
        localStorage.removeItem('postLoginRedirect');
      } else {
        navigate('/');
      }
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError(t('errorEmailInUse'));
      } else if (err.code === 'auth/weak-password') {
        setError(t('errorWeakPassword'));
      } else {
        setError(t('errorMessageDefault'));
      }
      console.error(err);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      onLoginSuccess(userCredential.user);
      
      // Gérer la redirection post-login
      const pendingCode = localStorage.getItem('pendingLobbyCode');
      const redirectPath = localStorage.getItem('postLoginRedirect');
      if (pendingCode) {
        navigate(`/lobby/${pendingCode}`);
        localStorage.removeItem('pendingLobbyCode');
      } else if (redirectPath) {
        navigate(redirectPath);
        localStorage.removeItem('postLoginRedirect');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(t('errorWrongCredentials'));
      console.error(err);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError(t('errorEnterEmail'));
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(t('resetEmailSent'));
      setError('');
      setTimeout(() => {
        setAuthMode('signin');
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(t('errorResetPassword'));
      console.error(err);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <div className="logo-container">
        <h1 className="logo-text">Quiz<span>VS</span></h1>
        <LanguageSwitcher />
      </div>

      {/* Mode rapide anonyme - Toujours visible en haut */}
      <div className="quick-anonymous-box">
        <div className="quick-anonymous-content">
          <div className="quick-anonymous-icon">⚡</div>
          <div className="quick-anonymous-text">
            <h3>{t('quickPlayTitle')}</h3>
            <p>{t('quickPlayDesc')}</p>
          </div>
          <form onSubmit={handleAnonymousLogin} className="quick-anonymous-form">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('usernamePlaceholder')}
              className="quick-input"
            />
            <button type="submit" className="quick-button">
              <span>{t('startButton')}</span>
              <span className="arrow">→</span>
            </button>
          </form>
        </div>
      </div>

      {/* Séparateur avec texte */}
      <div className="auth-separator">
        <span>{t('orFullAuth')}</span>
      </div>

      {/* Card d'authentification unifiée */}
      <div className="auth-box unified">
        <div className="auth-header">
          <h2>{authMode === 'reset' ? t('resetPasswordTitle') : t('authTitle')}</h2>
          <p className="auth-subtitle">
            {authMode === 'signin' && t('authSubtitleSignIn')}
            {authMode === 'signup' && t('authSubtitleSignUp')}
            {authMode === 'reset' && t('authSubtitleReset')}
          </p>
        </div>

        <div className="auth-content">
          <div className="auth-form">
            {/* Google Auth - Visible sauf en mode reset */}
            {authMode !== 'reset' && (
              <>
                <button onClick={handleGoogleLogin} className="social-button google-button">
                  <svg className="google-icon" viewBox="0 0 24 24">
                    <path fill="#EA4335" d="M5.26620003,9.76452941 C6.19878754,6.93863203 8.85444915,4.90909091 12,4.90909091 C13.6909091,4.90909091 15.2181818,5.50909091 16.4181818,6.49090909 L19.9090909,3 C17.7818182,1.14545455 15.0545455,0 12,0 C7.27006974,0 3.1977497,2.69829785 1.23999023,6.65002441 L5.26620003,9.76452941 Z" />
                    <path fill="#34A853" d="M16.0407269,18.0125889 C14.9509167,18.7163016 13.5660892,19.0909091 12,19.0909091 C8.86648613,19.0909091 6.21911939,17.076871 5.27698177,14.2678769 L1.23746264,17.3349879 C3.19279051,21.2936293 7.26500293,24 12,24 C14.9328362,24 17.7353462,22.9573905 19.834192,20.9995801 L16.0407269,18.0125889 Z" />
                    <path fill="#4A90E2" d="M19.834192,20.9995801 C22.0291676,18.9520994 23.4545455,15.903663 23.4545455,12 C23.4545455,11.2909091 23.3454545,10.5818182 23.1818182,9.90909091 L12,9.90909091 L12,14.4545455 L18.4363636,14.4545455 C18.1187732,16.013626 17.2662994,17.2212117 16.0407269,18.0125889 L19.834192,20.9995801 Z" />
                    <path fill="#FBBC05" d="M5.27698177,14.2678769 C5.03832634,13.556323 4.90909091,12.7937589 4.90909091,12 C4.90909091,11.2182781 5.03443647,10.4668121 5.26620003,9.76452941 L1.23999023,6.65002441 C0.43658717,8.26043162 0,10.0753848 0,12 C0,13.9195484 0.444780743,15.7301709 1.23746264,17.3349879 L5.27698177,14.2678769 Z" />
                  </svg>
                  {t('googleButton')}
                </button>
                <div className="divider" data-text={t('orDivider')}></div>
              </>
            )}

            {/* Formulaire selon le mode */}
            {authMode === 'reset' ? (
              <form onSubmit={handlePasswordReset}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  className="username-input"
                />
                <button type="submit" className="primary-button">
                  {t('sendResetButton')}
                </button>
              </form>
            ) : (
              <form onSubmit={authMode === 'signin' ? handleEmailSignIn : handleEmailSignUp}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  className="username-input"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('passwordPlaceholder')}
                  className="username-input"
                />
                
                {/* Lien mot de passe oublié */}
                {authMode === 'signin' && (
                  <div className="forgot-password-link">
                    <button 
                      type="button" 
                      onClick={() => setAuthMode('reset')}
                      className="link-button"
                    >
                      {t('forgotPasswordLink')}
                    </button>
                  </div>
                )}

                <button type="submit" className="primary-button">
                  {authMode === 'signin' ? t('signInButton') : t('signUpButton')}
                </button>
              </form>
            )}

            {/* Messages */}
            {error && <div className="message error-message">❌ {error}</div>}
            {success && <div className="message success-message">✅ {success}</div>}

            {/* Switch entre modes */}
            <div className="auth-switch">
              {authMode === 'signin' && (
                <p>
                  {t('noAccountYet')}{' '}
                  <button onClick={() => setAuthMode('signup')} className="link-button">
                    {t('signUpLink')}
                  </button>
                </p>
              )}
              {authMode === 'signup' && (
                <p>
                  {t('alreadyHaveAccount')}{' '}
                  <button onClick={() => setAuthMode('signin')} className="link-button">
                    {t('signInLink')}
                  </button>
                </p>
              )}
              {authMode === 'reset' && (
                <p>
                  {t('backToSignIn')}{' '}
                  <button onClick={() => setAuthMode('signin')} className="link-button">
                    {t('signInLink')}
                  </button>
                </p>
              )}
            </div>
          </div>
          
          {/* Liens légaux */}
          <div style={{ 
            textAlign: 'center', 
            marginTop: '20px', 
            fontSize: '12px', 
            color: '#666',
            borderTop: '1px solid #eee',
            paddingTop: '15px'
          }}>
            <p>
              En vous connectant, vous acceptez nos{' '}
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#805AD5', textDecoration: 'underline' }}
              >
                Conditions d'utilisation
              </a>
              {' '}et notre{' '}
              <a 
                href="/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#805AD5', textDecoration: 'underline' }}
              >
                Politique de confidentialité
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;